<template>
  <spending-summary
    :table-data="tableData"
    v-if="isEditable && rowIds.length > 0"
    @filterTableEmit="filterTable"
  />
  <v-container v-if="tableData && tableData.length > 0" class="tw-mt-4">
    <v-table density="compact">
      <thead>
        <tr>
          <th v-for="(header, index) in tableContents.headers" :key="index">
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in tableData"
          :key="index"
          :class="
            row.category === 'Unknown' || row.BCJL === 'Unknown'
              ? 'tw-bg-rose-100'
              : null
          "
        >
          <template v-if="!hideRows[index]">
            <td>
              {{
                isEditable
                  ? firebaseDateToString(row.fullDate)
                  : dateToString(row.fullDate)
              }}
            </td>
            <td>{{ row.desc }}</td>
            <td>{{ Number(row.amount).toFixed(2) }}</td>
            <td v-if="isEditable && rowIds">
              <v-select
                :items="
                  row.amount > 0
                    ? tableContents.category.amtPos
                    : tableContents.category.amtNeg
                "
                class="tw-m-auto"
                v-model="row.category"
                density="compact"
                @update:model-value="
                  updateCategory(row.category, rowIds[index])
                "
              />
            </td>
            <td v-else>
              {{ row.category }}
            </td>
            <td v-if="isEditable && rowIds">
              <v-select
                :items="tableContents.BCJL"
                v-model="row.BCJL"
                density="compact"
                @update:model-value="updateBCJL(row.BCJL, rowIds[index])"
              />
            </td>
            <td v-else>{{ row.BCJL }}</td>
            <td>{{ row.bank }}</td>
            <td v-if="isEditable && rowIds">
              <v-btn type="button" @click="deleteRow(index)" elevation="0">
                <delete-button />
              </v-btn>
            </td>
          </template>
        </tr>
      </tbody>
    </v-table>
  </v-container>
</template>
<style>
td .v-field {
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
}
.v-table {
  line-height: 1.2;
}
td {
  font-size: 0.8rem !important;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 30rem;
}
td .v-field__input {
  height: 2.5rem;
}
td .v-messages,
td .v-input__details {
  height: 0;
  min-height: 0 !important;
  padding: 0;
  margin: 0 !important;
  font-size: 0;
}
.v-input--density-compact {
  width: 150px;
  padding-top: 6px;
}
.v-field__append-inner {
  padding: 0;
  width: 1rem;
}

/* @media (min-width: 960px) */
.v-container {
  max-width: 1200px;
}
</style>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from "vue";
import { dateToString, firebaseDateToString } from "../util/date-util";
import { updateDoc, doc } from "firebase/firestore";
import { deleteDocId } from "../firebase/manageData";
import { db } from "../firebase/firestoreConfig";
import { Transaction } from "../data-types";
import SpendingSummary from "./SpendingSummary.vue";
import DeleteButton from "./DeleteButton.vue";
// import { addTransaction } from "../firebase/manageData";

export default defineComponent({
  name: "TransactionTable",
  components: {
    SpendingSummary,
    DeleteButton,
  },
  props: {
    tableData: { type: Array as PropType<Transaction[]>, default: () => [] },
    isEditable: { type: Boolean },
    rowIds: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props) {
    const filterTable = (filter: {
      filterCategory: string[];
      filterBCJL: string[];
    }) => {
      props.tableData.forEach((transaction: Transaction, index: number) => {
        hideRows.value[index] =
          !filter.filterBCJL.includes(transaction.BCJL) ||
          !filter.filterCategory.includes(transaction.category);
      });
    };

    const hideRows = ref<boolean[]>([]);

    // const showModal = ref<boolean>(false);

    // type NewEntry = {
    //   date: Date;
    //   description: string;
    //   amount: number;
    //   category: string;
    //   bank: string;
    //   BCJL: string;
    // };
    // const newEntry = ref<NewEntry>({
    //   date: new Date(),
    //   description: "",
    //   amount: 0,
    //   category: "",
    //   bank: "",
    //   BCJL: "",
    // });

    hideRows.value = Array(props.rowIds.length).fill(false);
    const tableContents = {
      headers: ["Date", "Description", "Amount", "Category", "Who", "Bank", ""],
      category: {
        amtNeg: ["Income", "Reimbursement", "Payment", "Transfer"],
        amtPos: [
          "Merchandise",
          "Grocery",
          "Restaurant",
          "Utility",
          "Gas",
          "Car",
          "Home",
          "Gym",
          "Fees",
          "Payment",
          "Travel",
          "Entertainment",
          "Transfer",
        ],
      },
      BCJL: [
        "BC",
        "JL",
        "BCJL",
        "BCJL (50/50)",
        "Not Include BC",
        "Not Include JL",
      ],
    };

    watch(props, () => {
      hideRows.value = Array(props.rowIds.length).fill(false);
    });

    const updateCategory = async (category: string, rowId: string) => {
      await updateDoc(doc(db, "transactions", rowId), {
        category,
      });
    };

    const updateBCJL = async (BCJL: string, rowId: string) => {
      await updateDoc(doc(db, "transactions", rowId), {
        BCJL,
      });
    };

    const deleteRow = (index: number) => {
      if (confirm("Do you want to delete this transaction?")) {
        deleteDocId(props.rowIds[index]);
        hideRows.value[index] = true;
      }
    };

    // const addRow = () => {
    //   console.log(newEntry.value);
    //   closeModal();
    // };

    // const closeModal = () => {
    //   showModal.value = false;
    //   newEntry.value = {
    //     date: new Date(),
    //     description: "",
    //     amount: 0,
    //     category: "",
    //     bank: "",
    //     BCJL: "",
    //   };
    // };

    return {
      tableContents,
      dateToString,
      firebaseDateToString,
      updateCategory,
      updateBCJL,
      deleteRow,
      hideRows,
      filterTable,
      // showModal,
      // addRow,
      // closeModal,
      // newEntry,
    };
  },
});
</script>
